import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as BillingActions from './billing.actions';
import { Store } from '@ngrx/store';
import { catchError, concatMap, map, withLatestFrom } from 'rxjs/operators';
import { of, combineLatest } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { PartnerUsageResponse } from '../../@shared/models/partner-usage.model';
import { LegacyPartnerApiService } from '../../@shared/services/legacypartnerapi.service';
import { ContractManagementApiService } from '../../@shared/services/contractmanagementapi.service';
import { BillingAddressResponse } from '../../@shared/models/billing_address.model';
import { BillingMetadataResponse } from '../../@shared/models/billing_metadata.model';
import { BillingContractResponse } from '../../@shared/models/billing_contract.model';
import { PartnerInvoicesResponse, PartnerInvoicesSummaryResponse } from '../../@shared/models/partner-invoice.model';
import { IMeta, IRequestOptions } from '../request-options.interface';
import { getPartnerInvoicesListConfig, getPartnerInvoicesMeta } from './billing.selectors';
import * as _ from 'lodash';

@Injectable()
export class BillingEffects {
  constructor(private readonly actions$: Actions, private readonly store: Store, private legacyApi: LegacyPartnerApiService, private readonly contractManagementApi: ContractManagementApiService) { }

  loadBillingAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadBillingAddress, BillingActions.addBillingAddressSuccess),
      concatMap(() =>
        this.contractManagementApi.get<Partial<BillingAddressResponse>>('/management/v2/partner/billingAddress').pipe(
          map((result) => BillingActions.loadBillingAddressSuccess({ data: result?.data?.attributes || {} })),
          catchError(error => of(BillingActions.loadBillingAddressFailure({ error })))
        )
      )
    )
  );

  addBillingAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.addBillingAddress),
      concatMap(action =>
        this.contractManagementApi.patch<Partial<BillingAddressResponse>>('/management/v2/partner/billingAddress', action.data).pipe(
          map((result) => BillingActions.addBillingAddressSuccess({ data: result.data || {} })),
          catchError(error => of(BillingActions.addBillingAddressFailure({ error })))
        )
      )
    )
  );

  loadBillingMetadata$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadBillingMetadata),
      concatMap(() =>
        this.contractManagementApi.get<Partial<BillingMetadataResponse>>('/management/v2/partner/billingMetadata').pipe(
          map((result) => BillingActions.loadBillingMetadataSuccess({ data: result.data?.attributes || {} })),
          catchError(error => of(BillingActions.loadBillingMetadataFailure({ error })))
        )
      )
    )
  );

  saveBillingMetadata$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.saveBillingMetadata),
      concatMap(action =>
        this.contractManagementApi.patch<Partial<BillingMetadataResponse>>('/management/v2/partner/billingMetadata', action.data as HttpParams).pipe(
          map((result) => BillingActions.saveBillingMetadataSuccess({ data: result.data || {} })),
          catchError(error => of(BillingActions.saveBillingMetadataFailure({ error })))
        )
      )
    )
  );

  loadContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadContract, BillingActions.saveBillingMetadataSuccess/*, BillingActions.validateSepaSuccess, BillingActions.addPaymentMethodSuccess*/),
      concatMap(() =>
        this.contractManagementApi.get<Partial<BillingContractResponse>>('/management/v2/partner').pipe(
          map((result) => BillingActions.loadContractSuccess({ data: result.data || {} })),
          catchError(error => of(BillingActions.loadContractFailure({ error })))
        )
      )
    )
  );

  loadPartnerInvoices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadPartnerInvoices),
      withLatestFrom(this.store.select(getPartnerInvoicesListConfig)),
      concatMap(([action, listConfig]) => {
        const config: IRequestOptions = listConfig;
        return this.contractManagementApi.get<PartnerInvoicesResponse>('/management/v2/partner/invoices?page[number]=' + (config.page! + 1) + '&page[size]=' + config.limit).pipe( // our components are developed for 0-indexed pages while api endpoint provides pages starting from 1
          map((result) => {
            const meta: IMeta = {
              count: Number(result.meta.count),
              limit: Number(result.meta.limit) || 20,
              page: (Number(result.meta.page) - 1) || 0, // our components are developed for 0-indexed pages while api endpoint provides pages starting from 1
              total: Number(result.meta.total)
            };

            return BillingActions.loadPartnerInvoicesSuccess({
              data: result.data,
              meta: meta
            })
          }),
          catchError(error => of(BillingActions.loadPartnerInvoicesFailure({ error })))
        )}
      )
    )
  );

  loadPartnerInvoicesShortSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadPartnerInvoicesShortSummary, BillingActions.loadPartnerInvoicesSuccess),
      concatMap(() =>
        this.contractManagementApi.get<PartnerInvoicesSummaryResponse>('/management/v3/customers/partner/##CURRENT_PARTNER_UUID##/invoices-indicator').pipe(
          map((result) => BillingActions.loadPartnerInvoicesShortSummarySuccess({ data: result })),
          catchError(error => of(BillingActions.loadPartnerInvoicesShortSummaryFailure({ error })))
        )
      )
    )
  );

  loadPartnerInvoicesNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadPartnerInvoicesNext),
      withLatestFrom(
        combineLatest([
          this.store.select(getPartnerInvoicesMeta),
          this.store.select(getPartnerInvoicesListConfig),
        ])
      ),
      concatMap(([action, [meta, listConfig]]) => {
        const nextPage = _.get(meta, 'page', 0) + 1;
        const totalPages = Math.ceil(
          _.get(meta, 'total', 0) / _.get(meta, 'limit', 20)
        );

        const config: IRequestOptions = { ...listConfig };

        if (nextPage <= totalPages) {
          config.page = nextPage;
        }

        return this.contractManagementApi.get<PartnerInvoicesResponse>('/management/v2/partner/invoices?page[number]=' + (config.page! + 1) + '&page[size]=' + config.limit).pipe( // our components are developed for 0-indexed pages while api endpoint provides pages starting from 1
          map((result) => {
            const meta: IMeta = {
              count: Number(result.meta.count),
              limit: Number(result.meta.limit) || 20,
              page: (Number(result.meta.page) - 1) || 0, // our components are developed for 0-indexed pages while api endpoint provides pages starting from 1
              total: Number(result.meta.total)
            };

            return BillingActions.loadPartnerInvoicesNextSuccess({
              data: result.data,
              meta: meta
            })
          }),
          catchError(error => of(BillingActions.loadPartnerInvoicesNextFailure({ error })))
        )}
      )
    )
  );

  loadPartnerUsage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadPartnerUsage),
      concatMap(() =>
        this.contractManagementApi.get<PartnerUsageResponse>('/management/v2/usage/partner/##CURRENT_PARTNER_UUID##').pipe(
          map((result) => BillingActions.loadPartnerUsageSuccess({ data: result.data })),
          catchError(error => of(BillingActions.loadPartnerUsageFailure({ error })))
        )
      )
    )
  );

  validateSepa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.validateSepa),
      concatMap(action =>
        this.contractManagementApi.post(`/management/v3/customers/partner/##CURRENT_PARTNER_UUID##/payment_methods/${action.paymentMethodUuid}/verify`, { verification_pin: action.verify_pin }).pipe(
          map(() => BillingActions.validateSepaSuccess()),
          catchError(error => of(BillingActions.validateSepaFailure({ error })))
        )
      )
    )
  );
}
